import { GetGrantsFilter, Grant } from './../../../common/models/grant';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';
import { CurrentUser } from '../../../common/models/user';
import { StorageService } from '../../../common/services/storage.service';
import { SearchService } from '../../../common/services/search.service';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { GenericDialogComponent } from '../../../common/components/dialog/dialog';
import { GrantService } from '../../../common/services/grant.service';
import { GrantsFilterBuilder } from '../../../common/components/filter/builders/grants-filter-builder';
import { FilterService } from '../../../common/components/filter/services/filter.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-grants',
    templateUrl: './grants.component.html',
    styleUrl: './grants.component.scss',
    imports: [MatCardModule,
        MatMenuModule,
        MatButtonModule,
        MatTableModule,
        MatPaginatorModule,
        MatTooltipModule,
        MatIconModule,
        MatDialogModule,
        MatSnackBarModule,
        CommonModule
    ]
})
export class GrantsComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    grants: Grant[] = [];
    filteredGrants: Grant[] = [];
    user: CurrentUser | null = null;

  
    isToggled = false;

    private readonly snackbarConfig: MatSnackBarConfig = {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top'
    };
  
    constructor(
      private router: Router,
      private filterBuilder: GrantsFilterBuilder,
      private grantService: GrantService,
      private storageService: StorageService,
      private searchService: SearchService,
      private filterService: FilterService,
      public themeService: CustomizerSettingsService,
      private dialog: MatDialog,
      private snackBar: MatSnackBar
    ) {}
  
    ngOnInit() {
     this.searchService.currentSearchTerm$.pipe(takeUntil(this.destroy$)).subscribe(term => {
       this.applySearch(term);
      }); 
      this.filterService.filterChanges$.pipe(takeUntil(this.destroy$)).subscribe((filters) => {
        this.filterService.setFiltersInScope(filters);
        this.fetchGrants();
     });
      this.themeService.isToggled$.pipe(takeUntil(this.destroy$)).subscribe(isToggled => {
        this.isToggled = isToggled;
      });
      this.storageService.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
        this.user = user;
        this.initializeFilters()
      });
    }

    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }

    initializeFilters(): void {
      const startYear = 2025;
      const currentYear = new Date().getFullYear();

      const filterConfig =  this.filterBuilder 
        .reset()
        .years(startYear)
        .build();
    
        this.filterService.setFilterConfig(filterConfig);
        this.filterService.restoreFiltersInScope({ year: currentYear });
    }
  
    fetchGrants(): void {
      const savedFilterValues = this.filterService.getFiltersInScope();
      const municipalityId = this.user?.municipalityId ?? ''; 

      const filter = new GetGrantsFilter(municipalityId, savedFilterValues?.year);

      this.grantService.getGrants(filter).pipe(takeUntil(this.destroy$)).subscribe({
        next: (data) => {
          this.grants = data;
          this.filteredGrants = data;
        },
        error: (error) => {
          console.error('Error fetching grants', error);
        }
      });
    }

    applySearch(searchTerm: string): void {
      searchTerm = searchTerm.trim().toLowerCase();
      if (searchTerm) {
        this.filteredGrants = this.grants.filter((grant: Grant) => {
          return (
            grant.name.toLowerCase().includes(searchTerm) ||
            grant.description.toLowerCase().includes(searchTerm) ||
            grant.year.toString().includes(searchTerm)
          );
        });
      } else {
        this.filteredGrants = [...this.grants];
      }
    }

    confirmDelete(grant: Grant): void {
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        width: '50%',
        data: {
          title: 'Potvrdite brisanje',
          message: `Jeste li sigurni da želite obrisati natječaj "${grant.name}"?`,
          confirmButtonText: 'Da',
          cancelButtonText: 'Ne',
        },
      });
  
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
        if (result === true) {
          this.deleteGrant(grant.id);
        }
      });
    }
  
    deleteGrant(grantId: string): void {
      this.grantService.deleteGrant(grantId).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.fetchGrants();
          this.snackBar.open('Natječaj je obrisan', 'Zatvori', this.snackbarConfig);
        },
        error: (error) => {
          console.error('Error deleting grant', error);
        }}
      );
    }

    goToCreateGrant(): void {
      this.router.navigate(['municipality', 'grants', 'create']);
    }
  
    goToEditGrant(grant: Grant): void {
      this.router.navigate(['municipality', 'grants',  grant.id, 'edit']);
    }
  }
